import { PropertiesData } from "@/types/tree";

/*
 * For a detailed view of the data structures look at the tests
 *
 * This function is also in PHP and they must both mirror each other's logic
 *
 * Logic:
 *
 * - Traverse the properties object recursively and find the "breakpointId" key. Then return its value
 * This way the returned properties will have "flattened" the breakpoint.
 * - If We're on the base breakpoint, also return any other value that isn't "responsive" (tied to a breakpoint)
 * - For non-base breakpoints, only return the values associated with that breakpoint
 * - If no values match the breakpoint, `undefined` is returned
 */
export function getFlattenedPropertiesByBreakpoint(
  propertiesData: PropertiesData,
  breakpointId: string,
  allBreakpointIds: string[],
  baseBreakpointId: string,
  whitelistedPropertyPaths?: string[]
): PropertiesData {
  function _getFattenPropertiesByDefault(
    properties: PropertiesData,
    parentPath: string
  ) {
    if (isObjectExcludingArray(properties)) {
      const accumulatorObj: Record<string, unknown> = {};

      for (const propertyKey in properties) {
        if (propertyKey === breakpointId) {
          return properties[propertyKey];
        }

        // Don't include data for other breakpoints that aren't the one we're flattening
        if (allBreakpointIds.includes(propertyKey)) continue;

        const result = _getFattenPropertiesByDefault(
          properties[propertyKey],
          `${parentPath ? `${parentPath}.` : ""}${propertyKey}`
        );

        if (result !== undefined) {
          accumulatorObj[propertyKey] = result;
        }
      }

      // Was anything added to the accumulator?
      return Object.keys(accumulatorObj).length ? accumulatorObj : undefined;
    } else if (Array.isArray(properties)) {
      const accumulatorArray: unknown[] = [];

      properties.forEach(property => {
        accumulatorArray.push(
          _getFattenPropertiesByDefault(property, `${parentPath}[]`)
        );
      });

      // for non-base breakpoints we can get an array of undefined. Remove it
      // e.g: "{tags: [1,2,3]}" becomes "{tags: [undefined, undefined, undefined]}"
      if (accumulatorArray.filter(value => value !== undefined).length === 0) {
        return undefined;
      }

      // Was anything added to the array?
      return accumulatorArray.length ? accumulatorArray : undefined;
    }

    const isCurrentPathWhitelisted =
      parentPath &&
      whitelistedPropertyPaths?.some(propertyPath =>
        parentPath.startsWith(propertyPath)
      );

    // only return non-responsive values if we're on the base breakpoint
    // A scalar (string, number, boolean, etc. But not an array or obj)
    return breakpointId === baseBreakpointId || isCurrentPathWhitelisted
      ? properties
      : undefined;
  }

  return _getFattenPropertiesByDefault(propertiesData, "");
}

export function isObjectExcludingArray(
  value: unknown
): /* todo: we are lying to typescript here, isn't there a better way to do this? the real problem is that properties data
is an any */ value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
